import { useRef } from "react";
import EmailEditor, { EditorRef, EmailEditorProps } from "react-email-editor";

import { Box, Button, Typography } from "@mui/material";

import MoovsDialog from "components/MoovsDialog";
import { NotificationSetting } from "types";
import { useScreenSize } from "globals/hooks";
import EmailEditorTips from "./EmailEditorTips";

type EmailEditorDialogProps = {
  open: boolean;
  onClose: () => void;
  notificationSetting: NotificationSetting;
  onSave: (design: any, html: string) => void;
};

const EmailEditorDialog = ({
  open,
  onClose,
  notificationSetting,
  onSave,
}: EmailEditorDialogProps) => {
  const emailEditorRef = useRef<EditorRef>(null);

  // Check screen size for desktop-only editor
  const { isMediumTabletView } = useScreenSize();

  // Check for required environment variables
  const unlayerProjectId = process.env.REACT_APP_UNLAYER_PROJECT_ID;
  const hasValidProjectId =
    unlayerProjectId && !isNaN(Number(unlayerProjectId));
  const hasMergeTags = notificationSetting.emailEditorProps?.mergeTags;

  const handleSave = () => {
    emailEditorRef.current?.editor?.exportHtml((data) => {
      onSave(data.design, data.html);
      onClose();
    });
  };

  const onLoad: EmailEditorProps["onLoad"] = (unlayer) => {
    const { emailDesignJson, unlayerTemplateId, emailEditorProps } =
      notificationSetting;

    if (!hasMergeTags) {
      return;
    }

    const { mergeTags } = emailEditorProps;

    // Load design or template based on available data
    if (emailDesignJson && !emailDesignJson.useDefault) {
      // Load custom design JSON
      unlayer.loadDesign(emailDesignJson);
    } else if (unlayerTemplateId) {
      // Load from template (either useDefault flag or no custom design)
      unlayer.loadTemplate(Number(unlayerTemplateId));
    }

    // Set merge tags using real data from useMergeTags hook
    const mergeTagsData: Record<string, any> = {};
    mergeTags.forEach((mergeTag) => {
      mergeTagsData[mergeTag.key] = {
        name: mergeTag.name,
        value: mergeTag.value,
        sample: mergeTag.sample, // Real sample data for preview
      };
    });
    unlayer.setMergeTags(mergeTagsData);
  };

  // Conditional content based on screen size and merge keys loading state
  const dialogContent = () => {
    // Desktop size check first (isMediumTabletView = true means NOT desktop)
    if (isMediumTabletView) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="70vh"
        >
          <Box sx={{ textAlign: "center", maxWidth: 400, px: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Email Editor Not Available
            </Typography>
            <Typography variant="body1" color="text.secondary">
              The email editor is only available on desktop devices. Please use
              a larger screen to edit email templates.
            </Typography>
          </Box>
        </Box>
      );
    }

    // Error state - merge keys
    if (!hasMergeTags) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="70vh"
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" color="error" sx={{ mb: 2 }}>
              Failed to load merge tags
            </Typography>
          </Box>
        </Box>
      );
    }

    // Error state - missing projectId, clear message for developer
    if (!hasValidProjectId) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="70vh"
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" color="error" sx={{ mb: 2 }}>
              Configuration Error
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Missing or invalid PROJECT_ID environment variable. Please check
              your environment configuration.
            </Typography>
          </Box>
        </Box>
      );
    }

    // Success state - render EmailEditor
    return (
      <Box height="100%">
        <EmailEditor
          ref={emailEditorRef}
          minHeight="100%"
          onLoad={onLoad}
          options={{
            displayMode: "email",
            projectId: Number(unlayerProjectId),
            appearance: {
              theme: "modern_light",
              panels: {
                tools: {
                  dock: "right",
                  collapsible: true,
                },
              },
            },
            mergeTagsConfig: {
              sort: true,
              autocompleteTriggerChar: "@",
            },
            features: {
              stockImages: true,
              preview: true,
              undoRedo: false,
              smartMergeTags: false, // only works with Scale Plan. See https://billing.unlayer.com/embed/362296
            },
          }}
        />
      </Box>
    );
  };

  return (
    <MoovsDialog
      open={open}
      onClose={() => {}} // prevents closing when clicking outside the dialog
      onXClick={onClose}
      dialogTitle="Edit Email Template"
      titleToolTip={<EmailEditorTips />}
      fullScreen
      customFooter={
        <Box display="flex" gap={2} justifyContent="flex-end" p={2}>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isMediumTabletView || !hasValidProjectId || !hasMergeTags}
          >
            Save Template
          </Button>
        </Box>
      }
    >
      {dialogContent()}
    </MoovsDialog>
  );
};

export default EmailEditorDialog;
